import HBIcon from "@/components/HBIcon";
import { BuyVipDialog } from "@/components/Vip";
import { isBigCompanyByUser } from "@/constants/vip";
import useAppStore from "@/context/store";
import DownloadProcess from "@/utils/downloadProcess";
import { isGdMerged } from "@/utils/gaoding";
import getTimeDuration from "@/utils/getTimeDuration";
import { usePageEnterRecord } from "@/utils/hooks/usePageEnterRecord";
import { isThirdPartyMaterial } from "@/utils/materialUtils";
import { PRESETS_CONDITION, stewardInstance } from "@/utils/steward";
import { useBoolean, useMount, useSessionStorageState } from "ahooks";
import { Alert } from "antd";
import classNames from "classnames";
import dayjs from "dayjs";
import { at } from "lodash";
import { useRouter } from "next/router";
import qs from "qs";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { When } from "react-if";
import { MaterialButton } from "../Full/Buttons";
import styles from "./index.module.less";

// 花瓣素材
function PinMaterial({ pin, searchWord, recordOperation, trackerData }) {
  const router = useRouter();
  const downloadDialogRef = useRef();

  // 在线编辑展示规则：https://doc.huanleguang.com/pages/viewpage.action?pageId=381429564
  const [showEditOnline, setShowEditOnline] = useState(false);

  usePageEnterRecord(PRESETS_CONDITION.HUABAN_MATERIAL_DETAILS_ENTER_TIMES, [
    pin,
  ]);

  useMemo(async () => {
    if (!pin) {
      setShowEditOnline(false);
      return;
    }
    const downloadProcess = new DownloadProcess({
      pin,
    });
    const hide = await downloadProcess.checkHideEditOnline();
    setShowEditOnline(!hide);
  }, [pin]);

  const { vips, user, config } = useAppStore(({ vips, user, config }) => ({
    vips,
    user,
    config,
  }));

  const info = pin.material_info;
  const material = pin.file_material;

  const [expireHasShow, setExpireHasShow] =
    useSessionStorageState("expireAlertHasShow");

  const copyright = useMemo(() => {
    if (
      material.material_category === "图片" ||
      material.material_category === "视频"
    ) {
      const copyright = String(
        at(pin, "material_info.content.extra.copyright")[0] || ""
      );

      // 包含"已取得""已获得"的进行展示，其余的则不展示"版权说明"
      if (
        copyright &&
        (copyright.includes("已取得") || copyright.includes("已获得"))
      ) {
        return copyright;
      }

      return "";
    }

    // 其他默认已获得
    return "已获得著作权/肖像权授权";
  }, [material.material_category, pin]);

  // 到期提醒
  const expireTip = useMemo(() => {
    if (!vips?.length || isBigCompanyByUser(user)) {
      return false;
    }

    let vip_expire = user?.extra?.material_vip_expire_at;

    if (isGdMerged(user)) {
      vip_expire = Math.max(
        ...vips.map(({ expires_at }) => new Date(expires_at))
      );
    }

    const days = dayjs(vip_expire).diff(dayjs(), "d", true);
    if (days <= (config?.expireTipDays || -1)) {
      const content = (
        <div>
          素材VIP将在
          {getTimeDuration({
            time: vip_expire,
            afterText: "后",
            reverse: true,
          })}
          到期{" "}
          <a
            className="primary-text-btn"
            onClick={() => {
              BuyVipDialog.open({
                isGdDialog: true,
                source_location: "续费_素材信息",
              });
              setExpireHasShow(true);
            }}
          >
            立即续费
          </a>
        </div>
      );
      return {
        days,
        content,
      };
    }
    return false;
  }, [config?.expireTipDays, setExpireHasShow, user, vips]);

  const files = useMemo(() => {
    const info = pin.material_info;
    const material = pin.file_material;

    let files = [];

    if (
      material.material_category === "平面模板" ||
      material.material_category === "视频模板"
    ) {
      const file = info?.files?.find((file) => file.file_type === "ORIGIN");
      if (file) {
        files.push(file);
      }
    } else if (material.material_category === "视频") {
      const mps = info?.content?.mps?.find((mps) => {
        return mps.name === "HD";
      });
      if (mps) {
        files.push({
          file_format: mps.file_format,
          file_size: mps.file_size,
          file_type: mps.name,
        });
      }
    } else {
      files = info.files;
    }

    return files;
  }, [pin]);

  const size = useMemo(() => {
    const material = pin.file_material;
    const info = pin.material_info;
    const content = info?.content;

    if (material.material_category === "平面模板") {
      if (content && content.layouts) {
        return {
          width: content.layouts[0].width,
          height: content.layouts[0].height,
        };
      } else {
        return {
          width: info?.preview?.width,
          height: info?.preview?.height,
        };
      }
    } else if (
      material.material_category === "图片" &&
      // 兼容一批背景图逻辑
      info.type !== "background"
    ) {
      // 兼容AIGC图片
      if (info?.authorization?.name?.endsWith("AIGC")) {
        return {
          width: info.preview.width,
          height: info.preview.height,
        };
      } else {
        if (content.originWidth && content.originHeight) {
          return {
            width: content.originWidth,
            height: content.originHeight,
          };
        }
      }
    }

    return null;
  }, [pin]);

  useEffect(() => {
    if (+router.query.pinId !== pin?.pin_id) {
      // 切换页面时，pin获取是异步的，所以需要等pin获取到了再执行
      return;
    }

    const clickDownload =
      router.query.clickDownload ||
      qs.parse(location.search.slice(1))?.clickDownload;

    // fix: 解决跳转到没有下载的素材时，下载弹窗不消失的问题
    if (downloadDialogRef.current?.destroy) {
      downloadDialogRef.current.destroy();
    }

    if (clickDownload && files[clickDownload]) {
      const downloadProcess = new DownloadProcess({
        pin,
        file: files[clickDownload],
        searchWord,
      });
      downloadProcess.processDownloadMaterial();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.query.clickDownload, pin?.pin_id]);

  useMount(() => {
    stewardInstance.getPositionDelivery({
      "/pins/:pinId": ["material_detail_dialog", "material_detail_nps"],
    });
  });

  const [infoExpand, { toggle: toogleExpand }] = useBoolean(false);

  if (files.length <= 0) {
    return null;
  }

  return (
    <div className={classNames(styles.material)}>
      {expireTip && !expireHasShow && (
        <Alert
          className={styles.expireAlert}
          message={expireTip.content}
          onClose={() => {
            setExpireHasShow(true);
          }}
          type="error"
          icon={<HBIcon type="ic_info" />}
          showIcon
          closable
          closeIcon={<HBIcon type="ic_clear2" />}
        />
      )}
      <div className={styles.top}>
        <a
          className={styles.left}
          data-gd-click="button_click"
          data-button-name="源文件可下载"
          data-board-id={pin?.board_id}
          data-content-id={pin?.pin_id}
          data-material-id={pin?.material_info?.id}
          href="/vip/info"
          target="_blank"
        >
          <div className={styles.bizTag} title="商用无忧"></div>
          <span className={styles.from}>
            来自
            <strong>
              &nbsp;
              {isThirdPartyMaterial(pin.material_info)
                ? "第三方合作"
                : "官方自营"}
            </strong>
            &nbsp;
          </span>
        </a>
        <div className={styles.right}>
          <div className={styles.id}>ID:{info.id}</div>
        </div>
      </div>
      <div
        className={classNames([
          styles.materialInfo,
          infoExpand ? styles.expand : null,
        ])}
        style={{
          maxHeight: infoExpand ? "150px" : "52px",
        }}
      >
        <div className={styles.param}>
          {copyright && (
            <div className={styles.item}>
              <div className={styles.label}>版权说明</div>
              <div className={styles.content}>
                {copyright}
                {material.material_category === "平面模板" && (
                  <>
                    ；<br />
                    未获得字体授权，需替换字体使用
                  </>
                )}
                ；
              </div>
            </div>
          )}
          <When condition={size}>
            <div className={styles.item}>
              <div className={styles.label}>
                规&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;格
              </div>
              <div className={styles.content}>
                {size?.width}x{size?.height}px
                <When
                  condition={
                    pin?.material_info?.dpi >= 45 &&
                    !isThirdPartyMaterial(pin?.material_info)
                  }
                >
                  {`（${pin?.material_info?.dpi} dpi）`}
                </When>
              </div>
            </div>
          </When>
          <div className={styles.item}>
            <div className={styles.label}>
              分&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;类
            </div>
            <div className={styles.content}>
              {material.material_category === "高端免抠"
                ? "免抠元素"
                : material.material_category}
            </div>
          </div>
          <When condition={material.material_category === "视频"}>
            <div className={styles.item}>
              <div className={styles.label}>
                分<span className={styles.space3}></span>辨
                <span className={styles.space3}></span>率
              </div>
              <div className={styles.content}>1080p</div>
            </div>
          </When>
        </div>

        <div className={styles.auth}>
          <div className={styles.item}>
            <div className={styles.label}>授权范围</div>
            <div className={styles.content} data-content-source="素材信息">
              <div>线上商用 I 线下印刷 I 户外/转售授权</div>
              <div>具体以您开通会员的授权范围为准</div>
            </div>
          </div>
        </div>

        <span className={styles.expandIcon} onClick={toogleExpand}>
          {infoExpand ? "收起" : "了解详情"}
          <HBIcon type="ic_arrow_down" />
        </span>
      </div>
      <MaterialButton
        pin={pin}
        searchWord={searchWord}
        showEditOnline={showEditOnline}
        recordOperation={recordOperation}
        trackerData={trackerData}
      />
    </div>
  );
}

export function PinMaterialRedirect({ pin, title }) {
  const { config } = useAppStore(({ config }) => ({
    config,
  }));

  return (
    <div className={styles.material}>
      <div className={styles.top}>
        <a
          className={styles.left}
          data-gd-click="button_click"
          data-button-name="源文件可下载"
          data-board-id={pin?.board_id}
          data-content-id={pin?.pin_id}
          data-material-id={pin?.file_material?.material_id}
          href="/vip/info"
          target="_blank"
        >
          <div className={styles.name}>花瓣素材</div>
          <div className={styles.link}>
            <span className={styles.text}>{config?.materialDetailText}</span>
            <HBIcon className={styles.icon} type="ic_Next21" />
          </div>
        </a>
      </div>
      <a
        className={styles.redirect}
        href={`/pins/${pin?.file_material?.pin_id}`}
        target="_blank"
      >
        <div className={styles.content}>
          <HBIcon className={styles.icon} type="error_outline" />
          <div className={styles.text}>当前素材已更新，前往最新版查看下载</div>
        </div>
        <HBIcon className={styles.arrow} type="ic_chevron_right" />
      </a>
    </div>
  );
}
export default memo(PinMaterial);
